import clsx from "clsx";
import { orderBy } from "lodash";
import type { InputHTMLAttributes } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  PremadeCharacter,
  PremadeInfoStatus,
  PremadeSourceContract,
} from "@dndbeyond/character-rules-engine";
import ArrowLeft from "@dndbeyond/fontawesome-cache/svgs/solid/arrow-left.svg";
import FilterIcon from "@dndbeyond/fontawesome-cache/svgs/solid/filter.svg";
import { Button, ButtonProps } from "@dndbeyond/ttui/lexicon/components/Button";
import { Dropdown, Option } from "@dndbeyond/ttui/lexicon/components/Dropdown";
import { InlineAlert } from "@dndbeyond/ttui/lexicon/components/InlineAlert";
import { Search } from "@dndbeyond/ttui/lexicon/components/Search";
import { Toast } from "@dndbeyond/ttui/lexicon/components/Toast";

import { MaxCharactersDialog } from "~/components/MaxCharactersDialog";
import { NoResultsFound } from "~/components/NoResultsFound";
import { characterServiceBaseUrl } from "~/config";
import { ddbBaseUrl } from "~/config";
import { claimCharacter, joinCampaign } from "~/helpers/characterServiceApi";
import { summon } from "~/helpers/summon";
import { NavigationUtils } from "~/tools/js/CharacterBuilder/utils";
import UserRoles from "~/tools/js/Shared/constants/UserRoles";
import { appEnvSelectors } from "~/tools/js/Shared/selectors";

import { Card } from "./Card";
import { FilterDrawer } from "./FilterDrawer";
import { Skeleton } from "./Skeleton";
import styles from "./styles.module.css";

// Opts the input out of the global input styles in styles/global.css
const LEXICON_INPUT_PROPS: InputHTMLAttributes<HTMLInputElement> & {
  "data-lexicon": string;
} = { "data-lexicon": "true" };

export const PremadeListingV2 = () => {
  const [premadeCharacters, setPremadeCharacters] = useState<
    Array<PremadeCharacter>
  >([]);
  const [loadingStatus, setLoadingStatus] = useState<
    "loading" | "loaded" | "error"
  >("loading");
  const [elevatedUserCharacters, setElevatedUserCharacters] = useState<
    Array<PremadeCharacter>
  >([]);
  const [showElevatedUserCharacters, setShowElevatedUserCharacters] =
    useState(false);
  const [showProgressForIndex, setShowProgressForIndex] = useState<
    number | null
  >(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedClasses, setSelectedClasses] = useState<Option[]>([]);
  const [selectedSpecies, setSelectedSpecies] = useState<Option[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<Option[]>([]);
  const [selectedSources, setSelectedSources] = useState<Option[]>([]);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);

  const userRoles = useSelector(appEnvSelectors.getUserRoles);
  const isElevatedUser =
    userRoles?.includes(UserRoles.LOREKEEPER) ||
    userRoles?.includes(UserRoles.ADMIN);

  const { characterSlotLimit, activeCharacterCount } = useSelector(
    appEnvSelectors.getCharacterSlots
  );
  const hasSlots =
    characterSlotLimit === null || activeCharacterCount < characterSlotLimit;

  const params = new URLSearchParams(globalThis.location.search);
  const campaignJoinCode = params.get("campaignJoinCode");
  const isAssigned = params.get("isAssigned") !== "false";
  const campaignId = params.get("campaignId");

  const filteredCharacters = premadeCharacters.filter((character) => {
    // Text search filter (existing)
    const matchesSearch = [
      character.characterData.name,
      character.characterData.characterLevel.toString(),
      `level ${character.characterData.characterLevel}`,
      `lvl ${character.characterData.characterLevel}`,
      character.characterData.primaryClassName,
      character.characterData.speciesName,
      ...character.sources.map((source) => source.sourceName),
    ]
      .join(" ")
      .toLowerCase()
      .includes(query.toLowerCase());

    // Class filter
    const matchesClass =
      selectedClasses.length === 0 ||
      selectedClasses.some(
        (opt) => opt.value === character.characterData.primaryClassName
      );

    // Species filter
    const matchesSpecies =
      selectedSpecies.length === 0 ||
      selectedSpecies.some(
        (opt) => opt.value === character.characterData.speciesName
      );

    // Level filter
    const matchesLevel =
      selectedLevels.length === 0 ||
      selectedLevels.some(
        (opt) => opt.value === character.characterData.characterLevel.toString()
      );

    // Source filter (check if ANY source matches)
    const matchesSource =
      selectedSources.length === 0 ||
      character.sources.some((source) =>
        selectedSources.some((opt) => opt.value === source.sourceId.toString())
      );

    return (
      matchesSearch &&
      matchesClass &&
      matchesSpecies &&
      matchesLevel &&
      matchesSource
    );
  });

  // Helper function to get the primary source of a character
  const getPrimarySource = (
    character: PremadeCharacter
  ): PremadeSourceContract => {
    return (
      character.sources.find((source) => source.isPrimary) || {
        sourceName: "Not Sourced",
        sourceId: 0,
        displayOrder: 9999,
        isPrimary: true,
      }
    );
  };

  // order premades by source display order, source name, and then character name
  // put unsourced characters at the end of the list
  const orderPremades = useCallback(
    (premades: Array<PremadeCharacter>, prioritizeDrafts = false) => {
      return orderBy(premades, [
        // Drafts should surface above archived premades
        ...(prioritizeDrafts
          ? [
              (char: PremadeCharacter) =>
                char.publishStatus === PremadeInfoStatus.DRAFT ? 0 : 1,
            ]
          : []),
        (char) => getPrimarySource(char).displayOrder,
        (char) => getPrimarySource(char).sourceName,
        (char) => char.characterData.name,
      ]);
    },
    []
  );

  const onClaim = async (characterId: number, key: number) => {
    try {
      setShowProgressForIndex(key);
      const response = await claimCharacter(characterId, isAssigned);
      let clonedId: number;
      if (response.ok) {
        const { id } = await response.json();
        clonedId = id;
      } else {
        if (response.status === 406) {
          setShowDialog(true);
          setShowProgressForIndex(null);
          return;
        } else {
          throw new Error("Error when cloning premade character!");
        }
      }
      let campaignId: string | null = null;

      if (campaignJoinCode) {
        const campaignResponse = await joinCampaign(campaignJoinCode, clonedId);

        if (campaignResponse.ok) {
          const data = await campaignResponse.json();
          campaignId = data.campaignId;
        } else {
          throw new Error(
            "Error when attempting to attach cloned character to campaign!"
          );
        }
      }

      // Redirect to character page or campaign page
      const characterUrl = NavigationUtils.getCharacterSheetUrl(clonedId);
      if (isAssigned) {
        window.location = characterUrl as string & Location;
      } else {
        // If, somehow, the campaignId is null, send them to their new character sheet. Otherwise, send them
        // back to the campaign page.
        window.location = campaignId
          ? (`${ddbBaseUrl}/campaigns/${campaignId}` as string & Location)
          : (characterUrl as string & Location);
      }
    } catch (e) {
      setShowToast(true);
      setShowProgressForIndex(null);
      throw e;
    } finally {
      setShowProgressForIndex(null);
    }
  };

  useEffect(() => {
    const fetchCharacters = async () => {
      try {
        const req = await summon(
          `${characterServiceBaseUrl}/premade${
            campaignId ? `?campaignId=${campaignId}` : ""
          }`
        );
        if (req.ok) {
          const json: { data: Array<PremadeCharacter> } = await req.json();
          setPremadeCharacters(
            json.data.filter(
              (char) => char.publishStatus === PremadeInfoStatus.PUBLISHED
            )
          );
          setElevatedUserCharacters(
            json.data.filter(
              (char) => char.publishStatus !== PremadeInfoStatus.PUBLISHED
            )
          );
        } else {
          setLoadingStatus("error");
        }
      } catch {
        setLoadingStatus("error");
        throw new Error("Non-OK response from GET published premades");
      } finally {
        setLoadingStatus("loaded");
      }
    };
    fetchCharacters();
  }, [campaignId]);

  const filterOptions = useMemo(() => {
    // Extract unique classes
    const classes = Array.from(
      new Set(
        premadeCharacters.map((char) => char.characterData.primaryClassName)
      )
    )
      .sort()
      .map((className) => ({ label: className, value: className }));

    // Extract unique species
    const species = Array.from(
      new Set(premadeCharacters.map((char) => char.characterData.speciesName))
    )
      .sort()
      .map((speciesName) => ({ label: speciesName, value: speciesName }));

    // Extract unique levels
    const levels = Array.from(
      new Set(
        premadeCharacters.map((char) => char.characterData.characterLevel)
      )
    )
      .sort((a, b) => a - b)
      .map((level) => ({ label: `Level ${level}`, value: level.toString() }));

    // Extract unique sources (deduplicate by sourceId)
    const sourcesMap = new Map<
      number,
      { name: string; displayOrder: number }
    >();
    premadeCharacters.forEach((char) => {
      char.sources.forEach((source) => {
        if (!sourcesMap.has(source.sourceId)) {
          sourcesMap.set(source.sourceId, {
            name: source.sourceName,
            displayOrder: source.displayOrder,
          });
        }
      });
    });

    const sources = Array.from(sourcesMap.entries())
      .sort((a, b) => {
        // Sort by displayOrder first, then name
        if (a[1].displayOrder !== b[1].displayOrder) {
          return a[1].displayOrder - b[1].displayOrder;
        }
        return a[1].name.localeCompare(b[1].name);
      })
      .map(([sourceId, { name }]) => ({
        label: name,
        value: sourceId.toString(),
      }));

    return { classes, species, levels, sources };
  }, [premadeCharacters]);

  const filters = [
    {
      key: "collection",
      label: "Collection",
      options: filterOptions.sources,
      selected: selectedSources,
      onChange: setSelectedSources,
    },
    {
      key: "class",
      label: "Class",
      options: filterOptions.classes,
      selected: selectedClasses,
      onChange: setSelectedClasses,
    },
    {
      key: "level",
      label: "Level",
      options: filterOptions.levels,
      selected: selectedLevels,
      onChange: setSelectedLevels,
    },
    {
      key: "species",
      label: "Species",
      options: filterOptions.species,
      selected: selectedSpecies,
      onChange: setSelectedSpecies,
    },
  ];

  // The same filters render inline on desktop and in the drawer below 1024px.
  // Both copies share state, so each needs its own id to keep DOM ids unique.
  const renderFilterDropdowns = (location: "page" | "drawer") =>
    filters.map(({ key, label, options, selected, onChange }) => {
      const dropdown = (
        <Dropdown
          key={key}
          id={`${key}-filter-${location}`}
          label={label}
          placeholder="All"
          multiple
          options={options}
          initialValues={selected}
          onChange={onChange}
        />
      );
      if (location === "drawer") return dropdown;
      // Show/hide lives on our own wrapper, since ttui's .dropdown styles load
      // later in the cascade and would override a class put on the Dropdown
      return (
        <div key={key} className={styles.filterSelect}>
          {dropdown}
        </div>
      );
    });

  const activeFilterCount =
    selectedClasses.length +
    selectedSpecies.length +
    selectedLevels.length +
    selectedSources.length;

  const clearAllFilters = () => {
    setSelectedClasses([]);
    setSelectedSpecies([]);
    setSelectedLevels([]);
    setSelectedSources([]);
  };

  const closeFilterDrawer = useCallback(() => setShowFilterDrawer(false), []);

  // The filter button is hidden on desktop, so don't leave the drawer open there
  useEffect(() => {
    if (!showFilterDrawer) return;
    const desktopQuery = globalThis.matchMedia?.("(min-width: 1024px)");
    if (!desktopQuery) return;
    if (desktopQuery.matches) {
      closeFilterDrawer();
      return;
    }
    const handleChange = (e: MediaQueryListEvent) => {
      if (e.matches) closeFilterDrawer();
    };
    desktopQuery.addEventListener("change", handleChange);
    return () => desktopQuery.removeEventListener("change", handleChange);
  }, [showFilterDrawer, closeFilterDrawer]);

  const orderedPremades = useMemo(
    () => orderPremades(filteredCharacters),
    [filteredCharacters, orderPremades]
  );

  const orderedElevatedUserPremades = useMemo(
    () => orderPremades(elevatedUserCharacters, true),
    [elevatedUserCharacters, orderPremades]
  );

  const renderPremadeList = (premadeList: Array<PremadeCharacter>) => {
    return (
      <div className={styles.grid}>
        {premadeList.map(
          (
            {
              characterData: {
                name,
                characterLevel,
                speciesName,
                primaryClassName,
              },
              definition: { longDescription, imageUrl, imageAltText },
              characterId,
              publishStatus,
            },
            i
          ) => {
            const isPublished = publishStatus === PremadeInfoStatus.PUBLISHED;
            const isClaimInProgress = isPublished
              ? showProgressForIndex === i
              : false;
            const ariaLabel = `${speciesName} ${primaryClassName}`;
            const primaryButtonText = isPublished ? "Choose" : "Edit";
            const previewLink = `${ddbBaseUrl}/characters/${characterId}${
              campaignJoinCode ? `?campaignJoinCode=${campaignJoinCode}` : ""
            }`;

            let primaryButtonProps: ButtonProps;
            if (isPublished) {
              primaryButtonProps = {
                onClick: (e) => {
                  e.stopPropagation();
                  onClaim(characterId, i);
                },
                disabled: !hasSlots || isClaimInProgress,
                "aria-label": isClaimInProgress
                  ? `Loading ${ariaLabel}`
                  : `Choose ${ariaLabel}`,
              };
            } else {
              primaryButtonProps = {
                href: `${ddbBaseUrl}/characters/${characterId}/builder`,
                "aria-label": `Edit ${ariaLabel}`,
              };
            }

            return (
              <Card
                key={i}
                data-testid="premade-character-card"
                characterName={name}
                characterLevel={characterLevel}
                characterId={characterId}
                publishStatus={publishStatus}
                className={clsx([
                  styles.premadeCard,
                  !hasSlots && styles.hideClaim,
                ])}
                speciesName={speciesName}
                primaryClassName={primaryClassName}
                sourceName={getPrimarySource(premadeList[i]).sourceName}
                longDescription={longDescription || ""}
                imageUrl={imageUrl || ""}
                imageAlt={imageAltText || `${speciesName} ${primaryClassName}`}
                primaryButtonText={primaryButtonText}
                primaryButtonProps={primaryButtonProps}
                isClaimInProgress={isClaimInProgress}
                previewLink={previewLink}
                onClick={(e) => {
                  if (isClaimInProgress) return;
                  // The preview link and primary button handle their own clicks
                  if ((e.target as HTMLElement).closest("a, button")) return;
                  // previewLink is a cross-origin URL (ddbBaseUrl), not an in-app route
                  window.location.assign(previewLink);
                }}
              />
            );
          }
        )}
      </div>
    );
  };

  return (
    <div className={styles.premadeListing} data-pagename="premade-listing">
      {(campaignJoinCode || campaignId) && (
        <Button
          className={styles.backButton}
          variant="leftArrow"
          color="secondary"
          href={`${ddbBaseUrl}/campaigns/${
            campaignJoinCode ? `join/${campaignJoinCode}` : campaignId
          }`}
        >
          <ArrowLeft />
          Back to{campaignJoinCode ? " Join" : ""} Campaign
        </Button>
      )}
      <div className={styles.content}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>
            <span className={styles.pageEyebrow}>Premade Characters</span>
            Pick a ready to play character.
          </h1>
          {!hasSlots && (
            <InlineAlert className={styles.inlineAlert} variant="warning">
              <p>
                Your party is full.{" "}
                <strong>
                  <a
                    className={styles.inlineAlertLink}
                    href={`${ddbBaseUrl}/store/subscribe#plans`}
                  >
                    Subscribe
                  </a>
                </strong>{" "}
                to unlock unlimited character creation.
              </p>
            </InlineAlert>
          )}
        </div>
        <div className={styles.filters}>
          {renderFilterDropdowns("page")}
          <Search
            className={styles.filterSearch}
            value={query}
            onSearch={setQuery}
            placeholder="Name, Class, Species, etc..."
            label="Search"
            inputProps={LEXICON_INPUT_PROPS}
          />
          <div className={styles.filterButtonWrapper}>
            <Button
              color="secondary"
              size="small"
              className={styles.filterButton}
              onClick={() => setShowFilterDrawer(true)}
              aria-label={
                activeFilterCount > 0
                  ? `Filters (${activeFilterCount} active)`
                  : "Filters"
              }
              aria-haspopup="dialog"
              aria-expanded={showFilterDrawer}
            >
              <FilterIcon />
            </Button>
            {activeFilterCount > 0 && (
              <span className={styles.filterCount} aria-hidden="true">
                {activeFilterCount}
              </span>
            )}
          </div>
        </div>

        {loadingStatus === "loading" && (
          <div className={styles.grid}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton className={styles.skeletonCard} key={i} />
            ))}
          </div>
        )}
        {/* Published Premades */}
        {loadingStatus === "loaded" &&
          (orderedPremades.length > 0 ? (
            renderPremadeList(orderedPremades)
          ) : (
            <NoResultsFound
              className={styles.noResults}
              message={`Empty tavern waits,\nNo champion claims this call,\nSeek another name.`}
            />
          ))}
        {/* Unpublished, admin only Premades */}
        {isElevatedUser && orderedElevatedUserPremades.length > 0 && (
          <div className={styles.elevatedUserContainer}>
            <Button
              className={styles.elevatedUserButton}
              color="secondary"
              onClick={() =>
                setShowElevatedUserCharacters(!showElevatedUserCharacters)
              }
            >
              {showElevatedUserCharacters ? "Hide" : "View"} Draft & Archived -
              ADMIN
            </Button>
            {showElevatedUserCharacters &&
              renderPremadeList(orderedElevatedUserPremades)}
          </div>
        )}
        <FilterDrawer
          open={showFilterDrawer}
          onClose={closeFilterDrawer}
          onReset={clearAllFilters}
          hasActiveFilters={activeFilterCount > 0}
        >
          {renderFilterDropdowns("drawer")}
        </FilterDrawer>
        {/* We know at the top level if someone doesn't have enough slots and displays a message at the top of the page and have the create character buttons disabled. The dialog here provides extra context if a user tries to claim a premade without enough slots. */}
        {showDialog && (
          <MaxCharactersDialog
            open={showDialog}
            onClose={() => setShowDialog(false)}
            useMyCharactersLink
          />
        )}
        {showToast && (
          <Toast
            onClose={() => setShowToast(false)}
            className={styles.toast}
            variant="error"
            message="An error occurred while creating your copy of this character."
            description={
              campaignJoinCode ? "Try joining the campaign again." : undefined
            }
            action={
              campaignJoinCode ? (
                <Button
                  href={`${ddbBaseUrl}/campaigns/${campaignJoinCode}`}
                  className={styles.dialogLink}
                >
                  Campaign Page
                </Button>
              ) : undefined
            }
          />
        )}
      </div>
    </div>
  );
};
